import { useEffect, useRef } from 'react';
import { createRoot, Root } from 'react-dom/client';

interface MapMarkerProps {
  map: google.maps.Map;
  position: google.maps.LatLngLiteral;
  title?: string;
  icon?: string | google.maps.Icon | google.maps.Symbol;
  onClick?: () => void;
  children?: React.ReactNode;
  draggable?: boolean;
  onDragEnd?: (position: google.maps.LatLngLiteral) => void;
}

const MapMarker: React.FC<MapMarkerProps> = ({
  map,
  position,
  title,
  icon,
  onClick,
  children,
  draggable = false,
  onDragEnd,
}) => {
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement>();
  const infoWindowRef = useRef<google.maps.InfoWindow>();
  const contentRef = useRef<HTMLDivElement>();
  const infoWindowContainerRef = useRef<HTMLDivElement>();
  const reactRootRef = useRef<Root>();

  useEffect(() => {
    const initMarker = async () => {
      if (!markerRef.current) {
        // Import the marker library dynamically
        const { AdvancedMarkerElement, PinElement } = (await google.maps.importLibrary(
          'marker'
        )) as google.maps.MarkerLibrary;

        // Create pin content based on icon prop
        let content: HTMLElement | undefined;

        if (icon && typeof icon === 'object' && 'path' in icon) {
          // Convert the old google.maps.Icon format to PinElement
          const symbol = icon;
          const pinElement = new PinElement({
            background: symbol.fillColor ?? '#1976D2',
            borderColor: symbol.strokeColor ?? '#FFFFFF',
            glyphColor: symbol.strokeColor ?? '#FFFFFF',
            scale: symbol.scale ? symbol.scale / 10 : 1.0,
          });
          content = pinElement.element;
        } else if (typeof icon === 'string') {
          // Custom HTML icon
          const div = document.createElement('div');
          div.innerHTML = icon;
          content = div;
        } else {
          // Default pin
          const pinElement = new PinElement({
            background: '#1976D2',
            borderColor: '#FFFFFF',
            glyphColor: '#FFFFFF',
          });
          content = pinElement.element;
        }

        // Create the marker
        const marker = new AdvancedMarkerElement({
          map,
          position,
          title,
          content,
          gmpDraggable: draggable,
        });
        markerRef.current = marker;

        // Store content reference for updates
        contentRef.current = content as HTMLDivElement;

        // Set up info window if children provided
        if (children) {
          infoWindowRef.current = new google.maps.InfoWindow();
        }

        // Add click listener
        if (onClick) {
          marker.addListener('click', onClick);
        }

        // Add click listener for info window
        if (children && infoWindowRef.current) {
          marker.addListener('click', () => {
            if (infoWindowRef.current && markerRef.current) {
              // Create a container div if it doesn't exist
              infoWindowContainerRef.current ??= document.createElement('div');

              // Create React root if it doesn't exist
              reactRootRef.current ??= createRoot(infoWindowContainerRef.current);

              // Render React children into the container
              reactRootRef.current.render(children);

              // Set the container as InfoWindow content
              infoWindowRef.current.setContent(infoWindowContainerRef.current);
              infoWindowRef.current.open({
                map,
                anchor: markerRef.current,
              });
            }
          });
        }

        // Add drag end listener
        if (onDragEnd && draggable) {
          marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
            if (event.latLng) {
              onDragEnd({
                lat: event.latLng.lat(),
                lng: event.latLng.lng(),
              });
            }
          });
        }
      }
    };

    void initMarker();

    return () => {
      // Clean up React root asynchronously to avoid unmounting during render
      if (reactRootRef.current) {
        const root = reactRootRef.current;
        setTimeout(() => {
          root.unmount();
        }, 0);
        reactRootRef.current = undefined;
      }

      // Clean up marker
      if (markerRef.current) {
        markerRef.current.map = null;
      }

      // Clean up info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- icon and position are handled in separate effects to avoid recreating marker
  }, [map, title, draggable, onClick, children, onDragEnd]);

  // Update marker position when prop changes
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.position = position;
    }
  }, [position]);

  // Update marker content when icon prop changes
  useEffect(() => {
    const updateIcon = async () => {
      if (markerRef.current && icon && typeof icon === 'object' && 'path' in icon) {
        const { PinElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
        const symbol = icon;
        const pinElement = new PinElement({
          background: symbol.fillColor ?? '#1976D2',
          borderColor: symbol.strokeColor ?? '#FFFFFF',
          glyphColor: symbol.strokeColor ?? '#FFFFFF',
          scale: symbol.scale ? symbol.scale / 10 : 1.0,
        });
        markerRef.current.content = pinElement.element;
        contentRef.current = pinElement.element as HTMLDivElement;
      }
    };

    void updateIcon();
  }, [icon]);

  return null;
};

export default MapMarker;
